import React, { useContext, useState } from 'react';

import {
  Box,
  Button,
  Chip,
  IconButton,
  Typography,
  makeStyles,
  Theme,
  createStyles,
} from '@material-ui/core';
import { FiArrowRight, FiMail, FiRefreshCw, FiSmartphone, FiX } from 'react-icons/fi';

import { deleteInviteRequest, resendInviteRequest } from '../../../../api/invites';
import { ChatCard, ChatCardBadge, ChatCardSection } from '../../../../components/Chat/kit';
import { LailaCardsContext } from './LailaCardsContext';
import { INVITES_PAGE, useOpenPage } from './fullForm';
import type { LailaCardViewProps } from './LailaCard';
import {
  INVITE_FILTERS,
  INVITE_STATUS,
  InviteFilter,
  InviteListData,
  InviteListRow,
  canAct,
  expiredToResend,
  filterCounts,
  filterInvites,
  initials,
  inviteActionError,
  invitedAgo,
  inviteeLabel,
  updateRow,
} from './inviteList';

// Rows shown before "Show more".
const FIRST_ROWS = 6;

// The company's open invites Laila listed (the invite_list card): resend or
// cancel one, or resend every expired one. Each is this page's own call to
// the Vyce API; Laila only hears what was done.
export const InviteListCard: React.FC<LailaCardViewProps> = ({ card }) => {
  const classes = useStyles();
  const { updateCard, noteForLaila } = useContext(LailaCardsContext);
  const openPage = useOpenPage();
  const [filter, setFilter] = useState<InviteFilter>('all');
  const [expanded, setExpanded] = useState(false);
  const [confirming, setConfirming] = useState<string | null>(null);
  const data = card.data as unknown as InviteListData;
  const counts = filterCounts(data.invites);
  const rows = filterInvites(data.invites, filter);
  const shown = expanded ? rows : rows.slice(0, FIRST_ROWS);
  const toResend = expiredToResend(data.invites);

  // Resends can finish in any order, so each updates the card as it is then.
  const patch = (id: string, change: Partial<InviteListRow>) =>
    updateCard(card.id, current => updateRow(current as unknown as InviteListData, id, change) as any);

  const resend = async (row: InviteListRow) => {
    patch(row.id, { state: 'resending', error: undefined });
    try {
      await resendInviteRequest(data.companyId, row.id);
      patch(row.id, { state: 'resent' });
      noteForLaila(`The user resent the invite to ${inviteeLabel(row)}.`);
    } catch (e) {
      patch(row.id, { state: 'failed', error: inviteActionError(e) });
    }
  };

  const resendExpired = async () => {
    for (const row of toResend) {
      await resend(row);
    }
  };

  const cancel = async (row: InviteListRow) => {
    setConfirming(null);
    patch(row.id, { state: 'cancelling', error: undefined });
    try {
      await deleteInviteRequest(data.companyId, row.id);
      patch(row.id, { state: 'cancelled' });
      noteForLaila(`The user cancelled the invite to ${inviteeLabel(row)}.`);
    } catch (e) {
      patch(row.id, { state: 'failed', error: inviteActionError(e) });
    }
  };

  const badge = (row: InviteListRow) => {
    switch (row.state) {
      case 'resent':
        return <ChatCardBadge tone="success">Resent</ChatCardBadge>;
      case 'cancelled':
        return <ChatCardBadge tone="neutral">Cancelled</ChatCardBadge>;
      case 'resending':
        return <ChatCardBadge tone="neutral">Resending</ChatCardBadge>;
      case 'cancelling':
        return <ChatCardBadge tone="neutral">Cancelling</ChatCardBadge>;
      default:
        return (
          <ChatCardBadge tone={INVITE_STATUS[row.status].tone}>
            {INVITE_STATUS[row.status].label}
          </ChatCardBadge>
        );
    }
  };

  return (
    <ChatCard
      label="Open invites"
      icon={<FiMail aria-hidden />}
      title="Open invites"
      subtitle={
        data.total > data.invites.length ? `The newest ${data.invites.length} of ${data.total}` : undefined
      }
      badge={String(counts.all)}
      badgeTone="neutral"
      footer={
        toResend.length || openPage ? (
          <Box className={classes.footer}>
            {!!toResend.length && (
              <Button
                variant="contained"
                color="primary"
                disableElevation
                startIcon={<FiRefreshCw aria-hidden />}
                onClick={resendExpired}
              >
                Resend {toResend.length} expired
              </Button>
            )}
            {openPage && (
              <Button
                color="primary"
                endIcon={<FiArrowRight aria-hidden />}
                onClick={() => openPage(INVITES_PAGE)}
              >
                All invites
              </Button>
            )}
          </Box>
        ) : undefined
      }
    >
      {!data.invites.length ? (
        <ChatCardSection>
          <Typography variant="body2" color="textSecondary">
            No open invites. Everyone you invited has accepted.
          </Typography>
        </ChatCardSection>
      ) : (
        <ChatCardSection>
          <Box className={classes.filters} role="group" aria-label="Filter by status">
            {INVITE_FILTERS.filter(({ value }) => value === 'all' || counts[value]).map(
              ({ value, label }) => (
                <Chip
                  key={value}
                  label={`${label} · ${counts[value]}`}
                  clickable
                  color={filter === value ? 'primary' : 'default'}
                  variant={filter === value ? 'default' : 'outlined'}
                  aria-pressed={filter === value}
                  onClick={() => {
                    setFilter(value);
                    setExpanded(false);
                  }}
                />
              )
            )}
          </Box>
          {shown.map(row => {
            const who = inviteeLabel(row);
            const done = row.state === 'cancelled';
            return (
              <Box key={row.id} className={classes.item}>
                <Box className={classes.row}>
                  <Box className={classes.avatar} aria-hidden>
                    {initials(row.name) || (row.phone ? <FiSmartphone /> : <FiMail />)}
                  </Box>
                  <Box className={classes.grow}>
                    <Typography variant="body2" className={done ? classes.done : undefined}>
                      {who}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" className={classes.meta}>
                      {[
                        row.type === 'staff' ? 'Admin' : 'Worker',
                        row.where,
                        `${invitedAgo(row.invitedAt)}${row.invitedBy ? `, by ${row.invitedBy}` : ''}`,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </Typography>
                  </Box>
                  {badge(row)}
                  {canAct(row) && (
                    <>
                      <IconButton size="small" aria-label={`Resend to ${who}`} onClick={() => resend(row)}>
                        <FiRefreshCw />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label={`Cancel the invite to ${who}`}
                        onClick={() => setConfirming(row.id)}
                      >
                        <FiX />
                      </IconButton>
                    </>
                  )}
                </Box>
                {row.state === 'failed' && row.error && (
                  <Typography variant="caption" color="error" role="alert" className={classes.indent}>
                    {row.error}
                  </Typography>
                )}
                {confirming === row.id && (
                  <Box className={classes.confirm} role="group" aria-label={`Cancel the invite to ${who}?`}>
                    <Typography variant="body2">
                      Cancel {who}’s invite? Their link stops working straight away. You can invite them
                      again any time.
                    </Typography>
                    <Box className={classes.buttons}>
                      <Button
                        variant="contained"
                        disableElevation
                        className={classes.danger}
                        onClick={() => cancel(row)}
                      >
                        Cancel invite
                      </Button>
                      <Button onClick={() => setConfirming(null)}>Keep it</Button>
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })}
          {rows.length > shown.length && (
            <Box>
              <Button color="primary" size="small" onClick={() => setExpanded(true)}>
                Show {rows.length - shown.length} more
              </Button>
            </Box>
          )}
        </ChatCardSection>
      )}
    </ChatCard>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    footer: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 },
    filters: { display: 'flex', flexWrap: 'wrap', gap: 6, paddingBottom: 6 },
    item: { borderBottom: `1px solid ${theme.palette.divider}`, '&:last-of-type': { borderBottom: 0 } },
    row: { display: 'flex', alignItems: 'center', gap: 8, minHeight: 56 },
    avatar: {
      flex: 'none',
      width: 34,
      height: 34,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 12,
      fontWeight: 600,
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.action.hover,
    },
    grow: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' },
    meta: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    done: { textDecoration: 'line-through', color: theme.palette.text.secondary },
    indent: { display: 'block', paddingLeft: 42, paddingBottom: 8 },
    confirm: {
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      margin: '0 0 10px 42px',
      padding: 12,
      borderRadius: 12,
      backgroundColor: theme.palette.action.hover,
    },
    buttons: { display: 'flex', flexWrap: 'wrap', gap: 8 },
    danger: {
      color: theme.palette.getContrastText(theme.palette.error.main),
      backgroundColor: theme.palette.error.main,
      '&:hover': { backgroundColor: theme.palette.error.dark },
    },
  })
);
